import { useState } from 'react';
import {
  Box, TextField, Snackbar, IconButton, Typography, InputAdornment, Link,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../common/components/LocalizationProvider';
import LoginLayout from './LoginLayout';
import { LoadingButton } from './components';
import getLoginTokens from './loginTokens';

const useStyles = makeStyles()((theme) => {
  const P = getLoginTokens(theme.palette.mode === 'dark', theme.palette.primary.main);
  return {
    formCard: {
      animation: 'loginSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards',
      opacity: 0,
    },
    customInput: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px !important',
        backgroundColor: `${P.inputBg} !important`,
        fontSize: '0.92rem',
      },
      '& .MuiOutlinedInput-root fieldset': {
        borderColor: `${P.inputBorder} !important`,
        borderWidth: '1.5px !important',
      },
      '& .MuiOutlinedInput-root:hover fieldset': {
        borderColor: `${P.inputBorderHover} !important`,
      },
      '& .MuiOutlinedInput-root.Mui-focused fieldset': {
        borderColor: `${P.primary} !important`,
        borderWidth: '1.5px !important',
        boxShadow: `0 0 0 3px ${P.focusShadow}`,
      },
      '& .MuiOutlinedInput-input': {
        padding: '0.85rem 1rem',
      },
    },
  };
});

const RegisterPage = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const P = getLoginTokens(isDark, theme.palette.primary.main);
  const t = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRegister = async (event) => {
    event.preventDefault();
    if (password !== confirm) {
      setSnackbar(t('registerPasswordMismatch'));
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, password }),
      });
      if (response.ok) {
        navigate('/');
      } else {
        setSnackbar(await response.text());
      }
    } catch (error) {
      setSnackbar(error.message);
    }
    setLoading(false);
  };

  const labelSx = {
    display: 'block',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: P.textPrimary,
    mb: 0.5,
  };

  return (
    <LoginLayout>
      <style>{`@keyframes loginSlideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }`}</style>

      <Box className={classes.formCard} sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: P.textPrimary,
            mb: 4,
            textAlign: 'center',
            letterSpacing: '-0.01em',
            fontSize: '1.55rem',
          }}
        >
          {t('loginRegister')}
        </Typography>

        <Box component="form" onSubmit={handleRegister} noValidate>
          {/* Nome */}
          <Box sx={{ mb: 1.5 }}>
            <Typography component="label" htmlFor="reg-name" sx={labelSx}>
              {t('sharedName')}
            </Typography>
            <TextField
              id="reg-name"
              required
              fullWidth
              placeholder={t('userName')}
              name="name"
              value={name}
              autoComplete="name"
              autoFocus
              variant="outlined"
              size="small"
              className={classes.customInput}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>

          {/* Email */}
          <Box sx={{ mb: 1.5 }}>
            <Typography component="label" htmlFor="reg-email" sx={labelSx}>
              {t('userEmail')}
            </Typography>
            <TextField
              id="reg-email"
              required
              fullWidth
              placeholder={t('loginEmailPlaceholder')}
              name="email"
              value={email}
              autoComplete="email"
              variant="outlined"
              size="small"
              className={classes.customInput}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Box>

          {/* Senha */}
          <Box sx={{ mb: 1.5 }}>
            <Typography component="label" htmlFor="reg-password" sx={labelSx}>
              {t('userPassword')}
            </Typography>
            <TextField
              id="reg-password"
              required
              fullWidth
              placeholder="••••••••••"
              name="password"
              value={password}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              variant="outlined"
              size="small"
              className={classes.customInput}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: P.icon, '&:hover': { color: P.primary } }}
                    >
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Confirmar senha */}
          <Box sx={{ mb: 2 }}>
            <Typography component="label" htmlFor="reg-confirm" sx={labelSx}>
              {t('userPasswordConfirmation')}
            </Typography>
            <TextField
              id="reg-confirm"
              required
              fullWidth
              placeholder="••••••••••"
              name="confirm"
              value={confirm}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              variant="outlined"
              size="small"
              className={classes.customInput}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Box>

          <LoadingButton
            onClick={handleRegister}
            loading={loading}
            disabled={!email || !password || !confirm || !name}
          >
            {t('loginRegister')}
          </LoadingButton>

          <Typography
            sx={{
              textAlign: 'center',
              mt: 2.5,
              fontSize: '0.85rem',
              color: P.textSecondary,
            }}
          >
            {`${t('loginAlreadyAccount')} `}
            <Link
              component="button"
              type="button"
              onClick={() => navigate('/login')}
              underline="none"
              sx={{
                color: P.primary,
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                '&:hover': { color: P.primaryHover },
              }}
            >
              {t('loginLogin')}
            </Link>
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={!!snackbar}
        message={snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar('')}
        action={(
          <IconButton size="small" color="inherit" onClick={() => setSnackbar('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      />
    </LoginLayout>
  );
};

export default RegisterPage;
